import { useMemo, useRef, useState } from 'react';

import {
  DropdownMenuRoot,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownCheckboxMenuItem,
  DropdownMenuTrigger,
} from 'common/ui/DropdownMenu';
import { Portal as DropdownMenuPortal } from '@radix-ui/react-dropdown-menu';

import { FilterChip } from './FilterChip';
import type { ChipSize, ChipColor, ChipBorder } from './Chip';
import { createContext } from '../util/createContext';
import type { CreateContextReturn } from '../util/createContext';
import type { FlattenSimpleInterpolation } from 'styled-components';
import { css } from 'styled-components';
import { rootTheme } from 'theme/rootTheme';

type TDropdownFilterChipContext = {
  open: boolean;
  setOpen: (open: boolean) => void;
  chipRef: {
    current: Nullable<HTMLButtonElement>;
  };
};

export const [
  DropdownFilterChipProvider,
  useDropdownFilterChip,
  DropdownFilterChipContext,
]: CreateContextReturn<TDropdownFilterChipContext> = createContext<TDropdownFilterChipContext>({
  strict: true,
  errorMessage:
    'useDropdownFilterChip: `context` is undefined. Seems you forgot to wrap component within the DropdownFilterChipProvider',
  name: 'DropdownFilterChipContext',
});

type DropdownFilterChipRootProps = {
  children: React.ReactNode;
};

export const DropdownFilterChipRoot = ({
  children,
}: DropdownFilterChipRootProps): React.ReactElement => {
  const chipRef = useRef<Nullable<HTMLButtonElement>>(null);
  const [open, setOpen] = useState(false);
  const contextValue = useMemo(
    () => ({
      open,
      setOpen,
      chipRef,
    }),
    [open]
  );

  return (
    <DropdownFilterChipProvider value={contextValue}>
      <DropdownMenuRoot modal={false} open={open}>
        {children}
      </DropdownMenuRoot>
    </DropdownFilterChipProvider>
  );
};

export type DropdownFilterChipProps = {
  size?: ChipSize;
  disabled?: boolean;
  hasSelection?: boolean;
  hasMenu?: boolean;
  onClear?: () => void;
  children: React.ReactNode;
  selectionColor?: ChipColor;
  onOpen?: () => Promise<void>;
  onClose?: () => void;
  title?: string;
  hideClear?: boolean;
  border?: ChipBorder;
};

export const DropdownFilterChip = ({
  onClear,
  children,
  hasMenu = true,
  onOpen,
  onClose,
  hideClear,
  border,
  ...rest
}: DropdownFilterChipProps): React.ReactElement => {
  const { open, setOpen, chipRef } = useDropdownFilterChip();

  return (
    <DropdownMenuTrigger asChild>
      <FilterChip
        {...rest}
        hasMenu={hasMenu}
        ref={chipRef}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();

          if (onOpen != null && !open) {
            onOpen();
          } else if (onClose != null && open) {
            onClose();
          }

          setOpen(!open);
        }}
        onClear={onClear}
        hideClear={hideClear}
      >
        {children}
      </FilterChip>
    </DropdownMenuTrigger>
  );
};

type DropdownFilterChipMenuProps = {
  children: React.ReactNode;
  cssStyle?: FlattenSimpleInterpolation;
  onInteractOutside?: (
    e: CustomEvent<{ originalEvent: PointerEvent }> | CustomEvent<{ originalEvent: FocusEvent }>
  ) => void;
  isScrollable?: boolean;
  canClose?: boolean;
};

export const DropdownFilterChipMenu = ({
  children,
  cssStyle,
  onInteractOutside,
  isScrollable = true,
  canClose = true,
}: DropdownFilterChipMenuProps): React.ReactElement => {
  const { setOpen, chipRef } = useDropdownFilterChip();

  const handleInteractionOutside = (
    e: CustomEvent<{ originalEvent: PointerEvent }> | CustomEvent<{ originalEvent: FocusEvent }>
  ) => {
    // We only want to close the dropdown menu on outside interaction if the
    // element the user is interacting with is not the dropdown trigger (or one of its children)
    const target = (e as CustomEvent<{ originalEvent: PointerEvent | FocusEvent }>).detail
      .originalEvent.target;
    if (canClose && target instanceof Node && !chipRef.current?.contains(target)) {
      setOpen(false);
      onInteractOutside != null && onInteractOutside(e);
    }
  };

  return (
    <DropdownMenuPortal>
      <DropdownMenuContent
        onEscapeKeyDown={() => {
          if (canClose) setOpen(false);
        }}
        onInteractOutside={handleInteractionOutside}
        align="start"
        css={css`
          max-height: 70vh;
          overflow: ${isScrollable ? 'auto' : 'hidden'};
          border-radius: ${rootTheme.radius.xs}px;
          z-index: 1400;
          ${cssStyle}
        `}
      >
        {children}
      </DropdownMenuContent>
    </DropdownMenuPortal>
  );
};

type DropdownFilterChipMenuItemProps = {
  value: string;
  isSelected: boolean;
  onSelect: (value: string) => void;
  children: React.ReactNode;
};

export const DropdownFilterChipMenuItem = ({
  value,
  isSelected,
  onSelect,
  children,
}: DropdownFilterChipMenuItemProps): React.ReactElement => {
  const { setOpen } = useDropdownFilterChip();

  return (
    <DropdownMenuItem
      isSelected={isSelected}
      onSelect={(e) => {
        e.stopPropagation();
        e.preventDefault();

        setOpen(false);
        onSelect(value);
      }}
    >
      {children}
    </DropdownMenuItem>
  );
};

type DropdownFilterChipCheckboxMenuItemProps = {
  group: string;
  label: string;
  value: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  color?: string;
};

export const DropdownFilterChipCheckboxMenuItem = ({
  group,
  label,
  value,
  checked,
  disabled = false,
  onChange,
  color,
}: DropdownFilterChipCheckboxMenuItemProps): React.ReactElement => {
  const { setOpen } = useDropdownFilterChip();

  return (
    <DropdownCheckboxMenuItem
      group={group}
      label={label}
      value={value}
      checked={checked}
      disabled={disabled}
      onCheckedChange={onChange}
      onDismiss={() => setOpen(false)}
      color={color}
    />
  );
};
